import { createIdleManager } from './IdleManager'
import { createUpdatePolicy } from './UpdatePolicy'
import { createVersionChecker, type VersionPayload } from './VersionChecker'
import { diffVersions } from './VersionComparator'
import { createVersionStore } from './VersionStore'

/**
 * Eventos do ciclo de versão. Existem para responder o que hoje ninguém
 * consegue: quantos participantes ficaram desatualizados, quantos reloads foram
 * adiados por fluxo crítico, e se o endpoint de versão está saudável.
 */
export type VersionEvent =
  | 'version_check_failed'
  | 'build_mismatch_detected'
  | 'reload_triggered'
  | 'reload_cancelled'
  | 'idle_timeout'
  | 'manual_update'
  | 'automatic_update'

export type TelemetrySink = (
  event: VersionEvent,
  payload?: Record<string, unknown>
) => void

type VersionServiceOptions = {
  currentVersion: string | null
  target?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>
  fetcher?: typeof fetch
  isVisible?: () => boolean
  /** Injetado para que o teste observe a intenção sem derrubar o runner. */
  reload?: () => void
  telemetry?: TelemetrySink
  now?: () => string
  enabled?: boolean
  idleWindowMs?: number
  pollIntervalMs?: number
  /**
   * Versões para as quais já se tentou recarregar. Injetável para teste; em
   * produção sobrevive ao reload via `sessionStorage` e morre com a aba.
   */
  attemptedVersions?: Set<string>
}

const ATTEMPTED_STORAGE_KEY = 'gamification.version.reloadAttempted'

/**
 * Recupera as tentativas de reload da sessão.
 *
 * Precisa sobreviver ao próprio reload: a trava só serve se, ao voltar, o app
 * lembrar que já tentou. `sessionStorage` morre com a aba, então um deploy
 * seguinte encontra a lousa limpa.
 */
const loadAttemptedVersions = (): Set<string> => {
  if (typeof sessionStorage === 'undefined') return new Set()

  try {
    const bruto = sessionStorage.getItem(ATTEMPTED_STORAGE_KEY)

    return new Set(bruto ? (JSON.parse(bruto) as string[]) : [])
  } catch {
    return new Set()
  }
}

const persistAttemptedVersions = (versions: Set<string>) => {
  if (typeof sessionStorage === 'undefined') return

  try {
    sessionStorage.setItem(
      ATTEMPTED_STORAGE_KEY,
      JSON.stringify(Array.from(versions))
    )
  } catch {
    // Storage cheio ou bloqueado não pode derrubar o serviço de versão.
  }
}

/**
 * Compõe checker, comparador, política, idle e store — e é o único lugar onde
 * `location.reload()` pode acontecer.
 *
 * Sem React por dentro: o provider é quem liga isto à árvore de componentes.
 * A separação não é purismo — é o que permite testar a fiação inteira, que era
 * justamente o que não tinha cobertura quando o app recarregou em loop em
 * produção (PR #85).
 */
export const createVersionService = ({
  currentVersion,
  target,
  fetcher,
  isVisible,
  reload = () => location.reload(),
  telemetry = () => {},
  now = () => new Date().toISOString(),
  enabled = true,
  idleWindowMs,
  pollIntervalMs,
  attemptedVersions = loadAttemptedVersions()
}: VersionServiceOptions) => {
  const store = createVersionStore({ currentVersion })
  const policy = createUpdatePolicy(store)

  /**
   * Ponto único de reavaliação. Não existe timer por tentativa de reload: os
   * três gatilhos (janela de idle, bloqueio saindo, navegação) chamam aqui, e
   * daqui sai — ou não — um único reload.
   */
  const avaliarReload = (
    contexto: { idle: boolean; safeNavigation?: boolean },
    origem: 'automatic_update' | 'manual_update'
  ) => {
    const { updateAvailable, blockingReasons, latestVersion, releaseType } =
      store.getState()

    if (!updateAvailable) return

    // Trava de tentativa. Sem ela, um reload que não resolve a divergência —
    // CDN servindo `index.html` velho, cache agressivo, deploy pela metade —
    // faz o app recarregar a cada navegação, que é a mesma classe de incidente
    // do PR #85. Com ela, o pior caso vira "fica na versão velha", visível na
    // telemetria em vez de destrutivo.
    if (latestVersion && attemptedVersions.has(latestVersion)) {
      telemetry('reload_cancelled', {
        latestVersion,
        releaseType,
        reason: 'already-attempted'
      })

      return
    }

    if (!policy.canReload()) {
      telemetry('reload_cancelled', {
        latestVersion,
        releaseType,
        blockingReasons: Array.from(blockingReasons)
      })

      return
    }

    const manual = origem === 'manual_update'

    if (!manual && !policy.shouldReloadAutomatically(contexto)) return

    if (latestVersion) {
      attemptedVersions.add(latestVersion)
      persistAttemptedVersions(attemptedVersions)
    }

    telemetry(origem, { latestVersion, releaseType })
    telemetry('reload_triggered', { latestVersion, releaseType, origem })
    reload()
  }

  const idleManager = createIdleManager({
    target,
    windowMs: idleWindowMs,
    onIdle: () => {
      telemetry('idle_timeout')
      avaliarReload({ idle: true }, 'automatic_update')
    }
  })

  const aoResultado = (payload: VersionPayload) => {
    const releaseType = diffVersions(store.getState().currentVersion, payload.version)

    store.getState().setLatest({
      latestVersion: payload.version,
      releaseType,
      lastCheck: now()
    })

    if (releaseType === null) return

    telemetry('build_mismatch_detected', {
      currentVersion: store.getState().currentVersion,
      latestVersion: payload.version,
      releaseType
    })

    // Uma atualização que chega com o app já ocioso não espera a próxima
    // janela — ela nunca mais fecharia, já que ninguém está interagindo.
    avaliarReload({ idle: idleManager.isIdle() }, 'automatic_update')
  }

  const checker = createVersionChecker({
    target,
    fetcher,
    isVisible,
    enabled,
    intervalMs: pollIntervalMs,
    onResult: aoResultado,
    onError: erro => {
      store.getState().markCheckFailed(now())
      telemetry('version_check_failed', { error: erro })
    }
  })

  // Um bloqueio saindo é gatilho de reavaliação: o reload adiado durante o quiz
  // acontece no instante em que a etapa termina, sem esperar mais 60s parado.
  const desinscrever = store.subscribe((state, anterior) => {
    if (state.blockingReasons === anterior.blockingReasons) return
    if (state.blockingReasons.size > 0) return

    avaliarReload({ idle: idleManager.isIdle() }, 'automatic_update')
  })

  return {
    store,
    policy,

    start: () => {
      if (!enabled) return

      idleManager.start()
      checker.start()
    },

    dispose: () => {
      desinscrever()
      idleManager.dispose()
      checker.dispose()
    },

    /** Chamado pela troca de rota: momento seguro mesmo sem idle. */
    notifyNavigation: () =>
      avaliarReload({ idle: false, safeNavigation: true }, 'automatic_update'),

    /** Ação "Atualizar agora" do modal de major. */
    updateNow: () => avaliarReload({ idle: true }, 'manual_update'),

    dismissUpdate: () => {
      const { latestVersion } = store.getState()

      if (latestVersion) store.getState().dismissUpdate(latestVersion)
    }
  }
}

export type VersionService = ReturnType<typeof createVersionService>
